import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button } from '@sunstone/ui';
import { Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { MarkdownRenderer } from '@/components/ui/MarkdownRenderer';
import { HighlightMatch } from './HighlightMatch';
import { getAnswerSnippet, matchQuestion } from './search';
import type { HelpCategory } from './types';

interface CategorySectionProps {
  category: HelpCategory;
  query: string;
  openSlugs: string[];
  onOpenSlugsChange: (slugs: string[]) => void;
}

export function CategorySection({
  category,
  query,
  openSlugs,
  onOpenSlugsChange,
}: CategorySectionProps) {
  const Icon = category.icon;

  function handleCopyLink(slug: string) {
    const url = `${window.location.origin}${window.location.pathname}#${slug}`;
    void navigator.clipboard.writeText(url);
    toast.success('Link copied');
  }

  return (
    <section id={category.id} className="scroll-mt-24">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">{category.label}</h2>
      </div>
      <Accordion
        type="multiple"
        value={openSlugs}
        onValueChange={onOpenSlugsChange}
        className="space-y-3"
      >
        {category.questions.map((question) => {
          const { inAnswer, inQuestion } = matchQuestion(question, query);
          const isOpen = openSlugs.includes(question.slug);
          const snippet =
            !inQuestion && inAnswer && !isOpen ? getAnswerSnippet(question, query) : undefined;
          return (
            <AccordionItem
              key={question.slug}
              value={question.slug}
              id={question.slug}
              className="scroll-mt-24 rounded-xl border-t border-r border-b border-l last:border-b border-border bg-card px-4 shadow-sm transition-shadow hover:shadow-md data-[state=open]:shadow-md"
            >
              <div className="flex items-center gap-1">
                <AccordionTrigger className="flex-1 text-base font-semibold">
                  <HighlightMatch text={question.question} query={query} />
                </AccordionTrigger>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyLink(question.slug);
                  }}
                  aria-label="Copy link to this question"
                >
                  <Link2 className="h-4 w-4" />
                </Button>
              </div>
              {snippet && <p className="-mt-2 mb-2 text-sm text-muted-foreground">…{snippet}</p>}
              <AccordionContent className="border-t border-border/60 pt-4">
                <MarkdownRenderer content={question.answer} highlightQuery={query} />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </section>
  );
}
